<template>
  <!--
    The jackpot popups of the v1 cockfight page (`pages/cock-fight.vue`), which
    the desktop client does not mount: the "you have an uncollected ticket"
    card, then the jackpot drawer (jackpot / history / instant-prize help).
    Both open inside the main layout (the centred app column), not against
    the window's edge: the drawer is pinned under the header to the column's
    right edge, sized from the column's width.
  -->
  <div class="jackpot-popups">
    <!--
      The shared Popup has no transition of its own (its `animation` prop is
      unused), so each one is mounted inside a transition here instead.
    -->
    <transition name="jackpot-popups-pop">
    <Popup
      v-if="isCollectionOpen"
      :active="true"
      animation="fade"
      :transparentBg="false"
      :fullsize="false"
      :hasBg="false"
      align="right"
      :borderRadiusBySide="['16px', '16px', '16px', '16px']"
      v-bind="{ modalContainerStyles: collectionContainerStyle }"
      @close="isCollectionOpen = false"
    >
      <JackPotCollectionPopup @jackpotPopup="collectTickets" />
    </Popup>
    </transition>

    <transition name="jackpot-popups-slide">
    <Popup
      v-if="isJackpotPopupOpen"
      :active="true"
      animation="fade"
      :transparentBg="false"
      :fullsize="false"
      align="right"
      valign="bottom"
      :hasBg="false"
      :showHeader="true"
      :borderRadiusBySide="['16px', '0', '0', '16px']"
      v-bind="{ modalContainerStyles: drawerContainerStyle }"
      @close="close"
    >
      <template v-slot:modalHeader>
        <div class="jackpot-popups__header">
          <Nav :imageURL="activePopupPage" :toURL="toURL" :popupLevel="popupLevel">
            {{ $t(activePopupPage) }}
          </Nav>
          <div v-if="activePopupPage === PopupPage.jackpot" class="jackpot-popups__nav-btns">
            <img src="@/assets/images/jackpot-history.svg" @click="showPage(PopupPage.jackpotHistory)" />
            <img
              v-if="config.enableJackpot"
              src="@/assets/images/jackpot-instant-prize.svg"
              @click="showPage(PopupPage.jackpotHelp)"
            />
          </div>
          <div class="jackpot-popups__close" @click="close">
            <img src="@/assets/images/close.webp" />
          </div>
        </div>
      </template>
      <JackPotPopup v-if="activePopupPage === PopupPage.jackpot" />
      <JackpotLogs
        v-if="activePopupPage === PopupPage.jackpotHistory"
        :logTickets="payoutTicketsLog"
        :ticketsLength="ownerTicketsLog.length || 0"
        hasHeader
        @loadMoreTickets="getOwnerTicketsLog"
      />
      <HelpContent v-if="activePopupPage === PopupPage.jackpotHelp" :helpSource="instantPrizeHelpGuide" />
    </Popup>
    </transition>
  </div>
</template>

<script>
import { some, findLast } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import Popup from '@/components/popup/index.vue';
import JackPotCollectionPopup from '@/components/popup/jackpot-collect.vue';
import JackPotPopup from '@/components/popup/jackpot.vue';
import JackpotLogs from '@/components/jackpot/ticket-logs.vue';
import HelpContent from '@/components/help/index.vue';
import Nav from '@/components/menus/nav.vue';
import { PopupPage } from '@/utils/index.model';
import { getLuckyDrawInstantPrizeHelpGuide } from '@/utils/game-help.ts';

// cock-fight.vue polls for new (unseen) tickets on the same interval.
const TICKET_POLL_MS = 120000;

export default {
  name: 'JackpotPopups',
  components: { Popup, JackPotCollectionPopup, JackPotPopup, JackpotLogs, HelpContent, Nav },
  props: {
    // Height of the header still on screen, so the drawer opens under it.
    topInset: {
      type: Number,
      default: 0,
    },
    // The app column in window pixels: { left, right } gutters and its width.
    frame: {
      type: Object,
      default: () => ({ left: 0, right: 0, width: 0 }),
    },
  },
  data() {
    return {
      PopupPage,
      isCollectionOpen: false,
      ticketPoll: null,
    };
  },
  computed: {
    ...mapGetters({
      isJackpotPopupOpen: 'menu/isJackpotPopupOpen',
      activePopupPage: 'menu/activePopupPage',
      config: 'general/config',
    }),
    ...mapGetters('jackpot', [
      'ownerTickets',
      'lastLuckyDraw',
      'ownerTicketsLog',
      'currentLuckyDraw',
      'ownerLuckyDrawTickets',
      'ownerInstantPrizeTickets',
    ]),
    hasUncollectTicket() {
      return some(this.ownerTickets, (ticket) => ticket.seenByMember === false);
    },
    uncollectLuckyDrawId() {
      const ticket = findLast(this.ownerLuckyDrawTickets, (t) => t.seenByMember === false);
      return ticket ? ticket.id : '';
    },
    uncollectInstantPrizeId() {
      const ticket = findLast(this.ownerInstantPrizeTickets, (t) => t.seenByMember === false);
      return ticket ? ticket.id : '';
    },
    payoutTicketsLog() {
      return this.ownerTicketsLog.filter(
        (t) =>
          (t.jackpot.type === 1 && t.rebate > 0) ||
          (t.jackpot.type === 0 && t.luckyDrawWinner && t.luckyDrawWinner.amount)
      );
    },
    instantPrizeHelpGuide() {
      return getLuckyDrawInstantPrizeHelpGuide(this.config.taxPercent, this.config.language);
    },
    toURL() {
      switch (this.activePopupPage) {
        case PopupPage.jackpotHistory:
        case PopupPage.jackpotLuckyDraw:
        case PopupPage.jackpotHelp:
          return PopupPage.jackpot;
        default:
          return '';
      }
    },
    popupLevel() {
      return this.activePopupPage === PopupPage.jackpot ? 1 : 2;
    },
    // The app column's width; the whole window until it has been measured.
    frameWidth() {
      return this.frame.width ? `${this.frame.width}px` : '100vw';
    },
    collectionContainerStyle() {
      return {
        position: 'relative',
        width: '90%',
        maxWidth: '350px',
        height: '320px',
        marginRight: `calc(${this.frame.right}px + ${this.frameWidth} * 0.15)`,
        marginTop: '20%',
      };
    },
    drawerContainerStyle() {
      const width = `calc(${this.frameWidth} * 0.45)`;
      return {
        position: 'relative',
        width,
        minWidth: width,
        maxWidth: width,
        marginRight: `${this.frame.right}px`,
        marginTop: `${this.topInset}px`,
        marginBottom: 0,
        height: `calc(100% - ${this.topInset}px)`,
      };
    },
  },
  created() {
    this.getOwnerTickets({ isSeen: false });
    this.ticketPoll = setInterval(() => this.getOwnerTickets({ isSeen: false }), TICKET_POLL_MS);
  },
  beforeDestroy() {
    clearInterval(this.ticketPoll);
  },
  methods: {
    ...mapActions('menu', ['updateMenu']),
    ...mapActions('jackpot', [
      'getOwnerTickets',
      'updateTicketsSeenStatus',
      'getPastWinners',
      'getProgressAmount',
      'resetTicketsPage',
      'getOwnerTicketsLog',
    ]),
    // The jackpot button: the collect card when a ticket is waiting, else the
    // jackpot drawer. Same as cock-fight.vue's collectJackpotHandler.
    open() {
      if (this.hasUncollectTicket) {
        this.isCollectionOpen = true;
        return;
      }
      this.getProgressAmount();
      this.resetTicketsPage();
      this.getPastWinners();
      this.getOwnerTicketsLog();
      if (this.currentLuckyDraw && this.currentLuckyDraw.id) {
        this.getOwnerTickets({ id: this.currentLuckyDraw.id, type: 'current-lucky-draw', isSeen: true });
      }
      if (this.lastLuckyDraw && this.lastLuckyDraw.id) {
        this.getOwnerTickets({ id: this.lastLuckyDraw.id, type: 'past-lucky-draw', isSeen: true });
      }
      this.isCollectionOpen = false;
      this.updateMenu({ isPopupOpen: false, activePopupPage: PopupPage.jackpot, isJackpotPopupOpen: true });
    },
    // "Collect" on the card: mark the waiting tickets seen, then open again.
    async collectTickets() {
      this.isCollectionOpen = false;
      if (this.uncollectLuckyDrawId) await this.updateTicketsSeenStatus(this.uncollectLuckyDrawId);
      if (this.uncollectInstantPrizeId) await this.updateTicketsSeenStatus(this.uncollectInstantPrizeId);
      await this.getOwnerTickets({ isSeen: false });
      setTimeout(() => this.open(), 300);
    },
    close() {
      this.updateMenu({ isJackpotPopupOpen: false });
    },
    showPage(page) {
      this.updateMenu({ activePopupPage: page });
    },
  },
};
</script>

<style lang="scss" scoped>
.jackpot-popups ::v-deep .global-modal {
  position: fixed;
  inset: 0;
  z-index: 4999;
}

/*
 * Open / close. The transition classes land on the Popup's overlay; the panel
 * inside it does the moving. The overlay's own opacity transition is what
 * Vue times the leave by, so it matches the panel's duration.
 */
$jackpot-anim: 0.28s;

.jackpot-popups-slide-enter-active,
.jackpot-popups-slide-leave-active,
.jackpot-popups-pop-enter-active,
.jackpot-popups-pop-leave-active {
  transition: opacity $jackpot-anim ease;

  ::v-deep .modal-container {
    transition: transform $jackpot-anim cubic-bezier(0.22, 0.61, 0.36, 1), opacity $jackpot-anim ease;
  }
}

.jackpot-popups-slide-enter,
.jackpot-popups-slide-leave-to,
.jackpot-popups-pop-enter,
.jackpot-popups-pop-leave-to {
  opacity: 0;
}

// Drawer: slides in from the layout's right edge.
.jackpot-popups-slide-enter,
.jackpot-popups-slide-leave-to {
  ::v-deep .modal-container {
    transform: translateX(48px);
    opacity: 0;
  }
}

// Collect card: grows in from slightly smaller.
.jackpot-popups-pop-enter,
.jackpot-popups-pop-leave-to {
  ::v-deep .modal-container {
    transform: scale(0.92);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .jackpot-popups-slide-enter-active,
  .jackpot-popups-slide-leave-active,
  .jackpot-popups-pop-enter-active,
  .jackpot-popups-pop-leave-active {
    &,
    ::v-deep .modal-container {
      transition: none;
    }
  }
}

.jackpot-popups__header {
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  justify-content: flex-start;
}

.jackpot-popups__nav-btns {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-right: 50px;

  img {
    width: 24px;
    height: 24px;
    margin-left: 8px;
    cursor: pointer;
  }
}

.jackpot-popups__close {
  width: 32px;
  height: 32px;
  position: absolute;
  right: 0;
  top: 0;
  display: block;
  background-image: linear-gradient(358deg, #bcbfc3 99%, #f0f0f0 1%);
  border-radius: 52%;
  cursor: pointer;

  img {
    width: 14px;
    height: 14px;
    margin: 9px;
  }
}
</style>
